/**
 * Map-editor EXPORT: turns the editor's blocks + spawn markers into the
 * MapDef-shaped payload the API stores — auto floor slab, oversized-box
 * tiling, surface-snapped spawns, and auto-placed pickups / jump pads /
 * vehicles. Pure (no DOM, no three.js), so it can be driven headless.
 */

/** The fields of an editor block that export reads. */
export interface ExportBlock { x: number; y: number; z: number; sx: number; sy: number; sz: number; color: string; tag?: string }
/** The fields of an editor spawn marker that export reads. */
export interface ExportSpawn { x: number; z: number; yaw: number }
type AutoPickupKind = "health" | "armor" | "grenade" | "speed" | "dmg" | "cloak" | "imperion" | "antigrav" | "decoy" | "jetpack" | "rocket" | "fartblaster" | "beigechest" | "waterchest" | "music";
type AutoVehicleKind = "kart" | "heli" | "speeder" | "artillery" | "gunship" | "interceptor";
type SurfaceSpawn = { x: number; y: number; z: number; yaw: number };

export function mapFootprint(blocks: readonly ExportBlock[]): { minX: number; maxX: number; minZ: number; maxZ: number; width: number; depth: number; span: number } {
  let minX = Infinity, maxX = -Infinity, minZ = Infinity, maxZ = -Infinity;
  for (const b of blocks) {
    minX = Math.min(minX, b.x - b.sx / 2); maxX = Math.max(maxX, b.x + b.sx / 2);
    minZ = Math.min(minZ, b.z - b.sz / 2); maxZ = Math.max(maxZ, b.z + b.sz / 2);
  }
  if (!Number.isFinite(minX)) { minX = -30; maxX = 30; minZ = -30; maxZ = 30; }
  const width = Math.max(1, maxX - minX), depth = Math.max(1, maxZ - minZ);
  return { minX, maxX, minZ, maxZ, width, depth, span: Math.max(width, depth) };
}

export function autoGameplay(blocks: readonly ExportBlock[], spawns: readonly ExportSpawn[]): {
  pickups: { p: [number, number, number]; kind: AutoPickupKind }[];
  vehicles: { p: [number, number, number]; yaw: number; kind: AutoVehicleKind }[];
  jumpPads: { p: [number, number, number]; power?: number; color?: string; fling?: number }[];
} {
  const fp = mapFootprint(blocks);
  const cx = (fp.minX + fp.maxX) / 2, cz = (fp.minZ + fp.maxZ) / 2;
  const topBlocks = blocks
    .filter((b) => b.sx >= 1.8 && b.sz >= 1.8 && b.y + b.sy / 2 >= -0.1)
    .sort((a, b) => Math.hypot(a.x - cx, a.z - cz) - Math.hypot(b.x - cx, b.z - cz));
  const used: [number, number][] = spawns.map((s) => [s.x, s.z]);
  const clearAbove = (x: number, y: number, z: number, half: number, tall: number): boolean => {
    for (const b of blocks) {
      if (Math.abs(x - b.x) >= b.sx / 2 + half || Math.abs(z - b.z) >= b.sz / 2 + half) continue;
      const bottom = b.y - b.sy / 2, top = b.y + b.sy / 2;
      if (y < top && y + tall > bottom) return false;
    }
    return true;
  };
  const take = (n: number, half: number, tall: number, spacing: number, minSpawn: number, high = false): [number, number, number][] => {
    const out: [number, number, number][] = [];
    const ordered = high
      ? [...topBlocks].sort((a, b) => (b.y + b.sy / 2) - (a.y + a.sy / 2) || Math.hypot(a.x - cx, a.z - cz) - Math.hypot(b.x - cx, b.z - cz))
      : topBlocks;
    for (const b of ordered) {
      if (out.length >= n) break;
      const x = b.x, z = b.z, y = b.y + b.sy / 2 + 0.2;
      if (x < fp.minX + 3 || x > fp.maxX - 3 || z < fp.minZ + 3 || z > fp.maxZ - 3) continue;
      if (!clearAbove(x, y, z, half, tall)) continue;
      if (spawns.some((s) => Math.hypot(s.x - x, s.z - z) < minSpawn)) continue;
      if (used.some(([ux, uz]) => Math.hypot(ux - x, uz - z) < spacing)) continue;
      out.push([x, y, z]); used.push([x, z]);
    }
    return out;
  };

  const scale = Math.max(0, Math.min(1, (fp.span - 55) / 170));
  const pickupCount = Math.round(7 + scale * 9);
  const padCount = Math.round(3 + scale * 5);
  const vehicleCount = fp.span < 70 ? 0 : Math.round(2 + scale * 3);
  const pickupKinds: AutoPickupKind[] = [
    "health", "armor", "grenade", "speed", "dmg", "cloak", "health", "armor",
    "imperion", "jetpack", "rocket", "decoy", "fartblaster", "beigechest", "waterchest", "antigrav", "music",
  ];
  const pickups = take(pickupCount, 1.1, 2.2, Math.max(7, fp.span / 13), 5).map((p, i) => ({
    p: [p[0], p[1] + 0.1, p[2]] as [number, number, number],
    kind: pickupKinds[i % pickupKinds.length],
  }));
  const jumpPads = take(padCount, 1.4, 4.5, Math.max(12, fp.span / 9), 6, true).map((p, i) => ({
    p: [p[0], p[1] - 0.05, p[2]] as [number, number, number],
    power: i % 3 === 0 ? 48 : 28,
    color: i % 3 === 0 ? "#a54dff" : "#ff8a2a",
    fling: i % 3 === 0 ? 0 : undefined,
  }));
  const vehicleKinds: AutoVehicleKind[] = ["kart", "speeder", "heli", "artillery", "interceptor", "gunship"];
  const vehicles = take(vehicleCount, 2.2, 2.4, Math.max(18, fp.span / 6), 8).map((p, i) => {
    const kind = vehicleKinds[i % vehicleKinds.length];
    return {
      p: [p[0], p[1] + (kind === "heli" ? 0.7 : 0.35), p[2]] as [number, number, number],
      yaw: (i % 4) * Math.PI * 0.5,
      kind,
    };
  });
  return { pickups, vehicles, jumpPads };
}

export function autoSpawns(blocks: readonly ExportBlock[], spawns: readonly ExportSpawn[]): SurfaceSpawn[] {
  const fp = mapFootprint(blocks);
  const cx = (fp.minX + fp.maxX) / 2, cz = (fp.minZ + fp.maxZ) / 2;
  const topBlocks = blocks
    .filter((b) => b.sx >= 2.2 && b.sz >= 2.2 && b.y + b.sy / 2 >= -0.1)
    .sort((a, b) => Math.hypot(b.x - cx, b.z - cz) - Math.hypot(a.x - cx, a.z - cz));
  const clearAt = (x: number, y: number, z: number): boolean => {
    for (const b of blocks) {
      if (Math.abs(x - b.x) >= b.sx / 2 + 0.65 || Math.abs(z - b.z) >= b.sz / 2 + 0.65) continue;
      const bottom = b.y - b.sy / 2, top = b.y + b.sy / 2;
      if (y < top && y + 2.1 > bottom) return false;
    }
    return true;
  };
  const escapeScore = (x: number, y: number, z: number): number => {
    let score = 0;
    for (let i = 0; i < 8; i++) {
      const a = i * Math.PI / 4;
      const dx = Math.sin(a), dz = Math.cos(a);
      for (const dist of [3, 6, 10]) {
        if (clearAt(x + dx * dist, y, z + dz * dist)) score++;
        else break;
      }
    }
    return score;
  };
  const spawnClear = (x: number, y: number, z: number): boolean => clearAt(x, y, z) && escapeScore(x, y, z) >= 10;
  const surfaceAt = (x: number, z: number): number | null => {
    let best: number | null = null;
    for (const b of topBlocks) {
      if (Math.abs(x - b.x) > b.sx / 2 - 0.5 || Math.abs(z - b.z) > b.sz / 2 - 0.5) continue;
      const y = b.y + b.sy / 2 + 0.15;
      if ((best === null || y > best) && spawnClear(x, y, z)) best = y;
    }
    return best;
  };
  if (spawns.length > 1) {
    return spawns.map((s): SurfaceSpawn => {
      const y = surfaceAt(s.x, s.z);
      if (y !== null) return { ...s, y };
      const nearest = topBlocks.find((b) => spawnClear(b.x, b.y + b.sy / 2 + 0.15, b.z));
      return nearest ? { x: nearest.x, z: nearest.z, yaw: s.yaw, y: nearest.y + nearest.sy / 2 + 0.15 } : { ...s, y: 0.15 };
    });
  }
  const targetN = Math.round(8 + Math.max(0, Math.min(1, (fp.span - 70) / 170)) * 8);
  const out: SurfaceSpawn[] = [];
  for (const b of topBlocks) {
    if (out.length >= targetN) break;
    const y = b.y + b.sy / 2 + 0.15;
    if (b.x < fp.minX + 4 || b.x > fp.maxX - 4 || b.z < fp.minZ + 4 || b.z > fp.maxZ - 4) continue;
    if (!spawnClear(b.x, y, b.z)) continue;
    const minSep = Math.max(12, fp.span / 8);
    if (out.some((s) => Math.hypot(s.x - b.x, s.z - b.z) < minSep)) continue;
    out.push({ x: b.x, z: b.z, y, yaw: Math.atan2(cx - b.x, cz - b.z) });
  }
  return out.length ? out : [{ x: 0, z: 0, y: 0.15, yaw: 0 }];
}

export function toMapData(blocks: readonly ExportBlock[], spawns: readonly ExportSpawn[], name: string, theme: "neon" | "day"): unknown {
  // bounding box of everything → an auto floor slab (top y=0) so it's grounded
  let mnx = -30, mxx = 30, mnz = -30, mxz = 30;
  for (const b of [...blocks.map((b) => ({ x: b.x, z: b.z, sx: b.sx, sz: b.sz })), ...spawns.map((s) => ({ x: s.x, z: s.z, sx: 4, sz: 4 }))]) {
    mnx = Math.min(mnx, b.x - b.sx / 2); mxx = Math.max(mxx, b.x + b.sx / 2);
    mnz = Math.min(mnz, b.z - b.sz / 2); mxz = Math.max(mxz, b.z + b.sz / 2);
  }
  const floor = { p: [(mnx + mxx) / 2, -0.5, (mnz + mxz) / 2], s: [mxx - mnx + 8, 1, mxz - mnz + 8], color: "#3a4152" };
  // The API caps every box SIZE component at 2000 (validateUserMap →
  // isVec3(s, 2000); the server mirrors it). Two producers here scale with the
  // map and can exceed it: the auto floor slab (footprint + 8 — a large
  // Minecraft import at crop×scale can be thousands of units wide) and a wide
  // same-colour terrain slab from the import greedy-merge. Either one made the
  // whole map fail to save with a misleading "each box needs p[3] and s[3]"
  // error. Tile any oversized box into a grid of ≤SPLIT_MAX pieces: adjacent
  // tiles share a face so collision/visuals are identical, and normal (in-cap)
  // boxes pass through untouched. Floor tiles keep p[1] < 0 so loadData still
  // skips them on re-open.
  const SPLIT_MAX = 1900;
  type ExportBox = { p: number[]; s: number[]; color: string; tag?: string };
  const splitBox = (box: ExportBox): ExportBox[] => {
    const [sx, sy, sz] = box.s;
    if (sx <= SPLIT_MAX && sy <= SPLIT_MAX && sz <= SPLIT_MAX) return [box];
    const nx = Math.ceil(sx / SPLIT_MAX), ny = Math.ceil(sy / SPLIT_MAX), nz = Math.ceil(sz / SPLIT_MAX);
    const tx = sx / nx, ty = sy / ny, tz = sz / nz;
    const x0 = box.p[0] - sx / 2, y0 = box.p[1] - sy / 2, z0 = box.p[2] - sz / 2;
    const out: ExportBox[] = [];
    for (let i = 0; i < nx; i++) for (let j = 0; j < ny; j++) for (let k = 0; k < nz; k++)
      out.push({ ...box, p: [x0 + tx * (i + 0.5), y0 + ty * (j + 0.5), z0 + tz * (k + 0.5)], s: [tx, ty, tz] });
    return out;
  };
  const rawBoxes: ExportBox[] = [floor, ...blocks.map((b) => ({ p: [b.x, b.y, b.z], s: [b.sx, b.sy, b.sz], color: b.color, ...(b.tag ? { tag: b.tag } : {}) }))];
  const boxes = rawBoxes.flatMap(splitBox);
  const sp = autoSpawns(blocks, spawns);
  const gameplay = autoGameplay(blocks, spawns);
  return {
    name: name.trim() || "My Map", theme,
    modes: ["gungame", "descent", "ffa", "onegun", "burnout", "arena", "treasure", "purge", "horde", "relay", "plunder"],
    boxes,
    spawns: sp.map((s) => ({ p: [s.x, s.y, s.z], yaw: s.yaw })),
    ...gameplay,
  };
}
